import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Virtual } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import LimitedEditionIcon from '@/assets/all-icons/limited-edition-icon.jsx';
import useProtectActionUnregistered from '@/hooks/useProtectActionUnregistered';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import {
  ARTWORK_PREFERENCE_SIGNALS,
  trackArtworkPreferenceInteraction,
} from '@/services/artworkPreferenceInteraction.service';
import LimitedEditionOwnersPopup from '@/shared-components/artwork/limited-edition-summary/LimitedEditionOwnersPopup';
import LimitedEditionOwnersPopupMobile from '@/shared-components/artwork/limited-edition-summary/LimitedEditionOwnersPopupMobile';
import Loader from '@/shared-components/loader/Loader';
import GenericBottomSheet from '@/shared-components/popups/components/GenericBottomSheet';
import SuartePlusPopup from '@/shared-components/popups/components/SuartePlusPopup';
import UnregisteredPopup from '@/shared-components/popups/unregistered-user-popup';
import AuctionUnregisteredPopup from '@/shared-components/popups/unregistered-user-popup/auction-unregistered-popup';
import { isRestrictedArtwork } from '@/utils/artworkVisibility';

import ArtworkNotFound from './components/ArtworkNotFound';
import ArtworkSlide from './components/ArtworkSlide';
import useArtwork from './hooks/useArtwork';
import useArtworkPageEnvironment from './hooks/useArtworkPageEnvironment';
import useArtworkSwiper from './hooks/useArtworkSwiper';
import useArtworkViewerOffers from './hooks/useArtworkViewerOffers';
import useLimitedEditionCollectors from './hooks/useLimitedEditionCollectors';
import useSoldPriceAccess from './hooks/useSoldPriceAccess';
import ArtworkAuctionMobile from './mobile-view/components/artwork-auction/ArtworkAuctionMobile';
import ArtworkMoreDetails from './mobile-view/components/artwork-more-details/ArtworkMoreDetails';
import ArtworkViewHeader from './mobile-view/components/artwork-view-header/ArtworkViewHeader';
import {
  getArtworkAuctionCta,
  isArtworkAuction,
  shouldShowLimitedEditionIcon,
} from './utils/artworkPagePresentation';
import {
  getArtworkHoldingType,
  getArtworkLimitedEditionOptions,
  getLimitedEditionSizeMap,
  needsResolvedArtworkOwnership,
} from './utils/artworkView.selectors';
import { getArtworkIdentityKeys } from './utils/offer.utils';

import './index.css';

const COMPACT_ARTWORK_LAYOUT_QUERY = '(max-width: 778px)';

export default function Artwork() {
  const { loading, error, artworks, shouldLoadMore, activeSlide, setActiveSlide } = useArtwork();
  const tArtworkView = useTranslations('common.shared.artwork_view.mobile');
  const { state, stateHandler } = useStateHandler();
  const location = useLocation();
  const navigate = useNavigate();
  const { slug: routeArtworkSlug } = useParams();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [auctionOpen, setAuctionOpen] = useState(false);
  const isMobileLayout = useMediaQuery({ query: COMPACT_ARTWORK_LAYOUT_QUERY });
  const useNativeScroll = isMobileLayout;
  const { isDirectAuctionEntry, isStandaloneVisit } = useArtworkPageEnvironment();
  const {
    visibleSlideIndex,
    onInit,
    onVirtualUpdate,
    onSlideChange,
    onTouchStart,
    onSetTranslate,
  } = useArtworkSwiper({
    activeSlide,
    artworkCount: artworks?.data?.length,
    setActiveSlide,
    useNativeScroll,
  });
  const viewerSessionId = state?.user_session?._id ?? state?.user_session?.id ?? null;
  const openAuthentication = useCallback(() => {
    // This page-level controller remains mounted while the mobile details sheet is closed.
    stateHandler.set('showAuthPopup', 'login');
  }, [stateHandler]);
  const {
    revealedArtworks,
    pendingSlug: soldPricePendingSlug,
    plusPopupOpen: soldPricePopupOpen,
    closePlusPopup: closeSoldPricePopup,
    unlockSoldPrice,
    refreshAfterPlus: refreshSoldPriceAfterPlus,
  } = useSoldPriceAccess({
    viewerSessionId,
    activeSlug: artworks?.data?.[activeSlide]?.slug,
    onAuthenticationRequired: openAuthentication,
  });
  const viewerOffersByArtworkId = useArtworkViewerOffers({
    viewerSessionId,
    isMobileLayout,
    detailsOpen,
    auctionOpen,
  });

  const artworkSlides = useMemo(() => {
    const source = Array.isArray(artworks?.data) ? artworks.data : [];

    return source.map((artworkData) => {
      const revealed = revealedArtworks[artworkData.slug];
      const pricedArtwork = revealed && revealed._id === artworkData._id
        ? { ...artworkData, ...revealed, purchase_context: undefined }
        : artworkData;
      const activeViewerOffer = getArtworkIdentityKeys(pricedArtwork)
        .map((identityKey) => viewerOffersByArtworkId[identityKey] ?? null)
        .find(Boolean) ?? null;

      if (!activeViewerOffer) {
        return pricedArtwork;
      }

      return {
        ...pricedArtwork,
        _activeViewerOffer: activeViewerOffer,
      };
    });
  }, [artworks?.data, revealedArtworks, viewerOffersByArtworkId]);

  const activeArtworkData = artworkSlides?.[activeSlide] || artworkSlides?.[0];
  const shouldAutoOpenLimitedEditionCollectors = useMemo(
    () => (
      new URLSearchParams(location.search).get('limited_edition_owners') === '1'
      && (!routeArtworkSlug || routeArtworkSlug === activeArtworkData?.slug)
    ),
    [activeArtworkData?.slug, location.search, routeArtworkSlug],
  );
  const {
    collectors: limitedEditionCollectors,
    openCollectors: openLimitedEditionCollectors,
    ownersPopupOpen,
    closeOwnersPopup,
    suartePlusPopupOpen,
    closeSuartePlusPopup,
    handleSuartePlusActivated,
  } = useLimitedEditionCollectors({
    artworkSlug: activeArtworkData?.slug,
    viewer: state.user_session,
    onAuthenticationRequired: openAuthentication,
    autoOpen: shouldAutoOpenLimitedEditionCollectors,
  });

  const activeArtworkIsAuction = useMemo(
    () => isArtworkAuction(activeArtworkData),
    [activeArtworkData?.artwork_about, activeArtworkData?.artwork_auction],
  );

  const shouldUseAuctionPopup = isDirectAuctionEntry && activeArtworkIsAuction;

  const { privateActionHandlerUnregistered, showPopup } = useProtectActionUnregistered({
    typeOfProtection: "USER_LIMIT_VIEW",
    popupDelayMs: shouldUseAuctionPopup ? 0 : undefined,
  });

  const holdingType = getArtworkHoldingType(activeArtworkData);
  const limitedEditionOptions = useMemo(
    () => getArtworkLimitedEditionOptions(activeArtworkData, {
      fallbackHoldingMetadata: holdingType === 'limited_edition',
    }),
    [activeArtworkData, holdingType],
  );

  const showLimitedEditionIcon = shouldShowLimitedEditionIcon(
    artworkSlides[visibleSlideIndex] ?? activeArtworkData,
  );
  const footerIsSettling = useNativeScroll && visibleSlideIndex !== activeSlide;
  const limitedEditionSizeMap = useMemo(
    () => getLimitedEditionSizeMap(limitedEditionOptions),
    [limitedEditionOptions],
  );
  const isAdminOnlyArtwork = useMemo(
    () => isRestrictedArtwork(activeArtworkData),
    [activeArtworkData],
  );

  const auctionCta = useMemo(
    () => getArtworkAuctionCta(activeArtworkData),
    [activeArtworkData?.artwork_about, activeArtworkData?.artwork_auction],
  );

  // Hide app navigation when in artwork view
  useEffect(() => {
    document.body.classList.add('hide-app-nav');
    return () => {
      document.body.classList.remove('hide-app-nav');
    };
  }, []);

  useEffect(() => {
    setAuctionOpen(false);
    setDetailsOpen(false);
  }, [activeArtworkData?._id]);

  function handleClick(event) {
    privateActionHandlerUnregistered(event);
    if (event.defaultPrevented) {
      return;
    }
  }

  if (loading || (!error && needsResolvedArtworkOwnership(activeArtworkData, viewerSessionId))) {
    return <Loader />;
  } else if (error) {
    return <ArtworkNotFound />;
  } else {
    return (
      <>
        {/* Fixed header shown across all artwork slides (outside Swiper to avoid transform issues) */}
        <ArtworkViewHeader artworkData={activeArtworkData} />
        {showPopup && (
          shouldUseAuctionPopup ? (
            <AuctionUnregisteredPopup showPopUp={showPopup} />
          ) : (
            <UnregisteredPopup showPopup={showPopup} />
          )
        )}
        <Swiper 
          key={useNativeScroll ? 'native-scroll' : 'transform-slider'}
          className={`artwork__swiper${useNativeScroll ? ' artwork__swiper--native-scroll' : ''}`}
          direction='vertical'
          cssMode={useNativeScroll}
          allowTouchMove={true}
          touchRatio={1}
          speed={useNativeScroll ? 400 : 900}
          noSwiping={true}
          noSwipingClass="artwork-slider-lock"
          onInit={onInit}
          onVirtualUpdate={onVirtualUpdate}
          onSlideChange={onSlideChange}
          onTouchStart={onTouchStart}
          onSetTranslate={onSetTranslate}
          modules={[Virtual]}
          onTransitionEnd={handleClick}
          virtual
        >
          {artworkSlides.map((artworkData, index) => {
            const isAuctionArtwork = isArtworkAuction(artworkData);
            const useAuctionVariant = isDirectAuctionEntry && isAuctionArtwork;
            return (
              <SwiperSlide
                key={artworkData.product_id ? artworkData.product_id : artworkData._id}
                virtualIndex={index}
              >
                <ArtworkSlide
                  artworkData={artworkData}
                  isMobileLayout={isMobileLayout}
                  isActive={index === activeSlide}
                  hasPrev={index !== 0}
                  hasNext={index !== artworks.data.length - 1}
                  displayLoader={shouldLoadMore && index === artworks.data.length - 1}
                  detailsOpen={detailsOpen}
                  openLimitedEditionCollectors={openLimitedEditionCollectors}
                  onUnlockSoldPrice={unlockSoldPrice}
                  soldPricePending={soldPricePendingSlug === artworkData.slug}
                  useAuctionVariant={useAuctionVariant}
                  isStandaloneVisit={isStandaloneVisit}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
        {isMobileLayout && (
          <div
            className={`artwork-view-footer${
              auctionCta.isAuction ? ' artwork-view-footer--auction' : ''
            }${footerIsSettling ? ' artwork-view-footer--settling' : ''}`}
          >
            <button
              type="button"
              className="artwork-view-footer__button artwork-view-more-details-button"
              disabled={footerIsSettling}
              onClick={() => {
                const activeArtworkSlug =
                  artworks?.data?.[activeSlide]?.slug
                  ?? artworks?.data?.[activeSlide]?.product_slug
                  ?? artworks?.data?.[activeSlide]?.artwork_slug
                  ?? null;
                if (activeArtworkSlug) {
                  trackArtworkPreferenceInteraction({
                    slug: activeArtworkSlug,
                    signal: ARTWORK_PREFERENCE_SIGNALS.MORE_DETAILS_MOBILE,
                    metadata: { source: 'artwork-view-footer__button artwork-view-more-details-button' },
                  });
                }
                setAuctionOpen(false);
                setDetailsOpen(true);
              }}
            >
              <span>{tArtworkView('more_details_button')}</span>
              {showLimitedEditionIcon ? (
                <LimitedEditionIcon className="artwork-view-more-details-button__icon" />
              ) : null}
            </button>
            {auctionCta.isAuction && (
              <button
                type="button"
                className="artwork-view-footer__button artwork-view-see-auction-button"
                disabled={footerIsSettling}
                onClick={() => {
                  setDetailsOpen(false);
                  setAuctionOpen(true);
                }}
              >
                <span>{auctionCta.label}</span>
              </button>
            )}
          </div>
        )}

        {/* Single bottom-sheet bound to the active artwork */}
        <GenericBottomSheet
          className="artwork-more-details__bottom-sheet"
          open={detailsOpen}
          close={() => setDetailsOpen(false)}
          dismissOnSwipeDown
          onTouchStart={(event) => event.stopPropagation()}
          onTouchEnd={(event) => event.stopPropagation()}
          onWheel={(event) => event.stopPropagation()}
        >
          <ArtworkMoreDetails
            artworkData={artworkSlides[activeSlide]}
            onOpenLimitedEditionCollectors={openLimitedEditionCollectors}
            onUnlockSoldPrice={unlockSoldPrice}
            soldPricePending={soldPricePendingSlug === activeArtworkData?.slug}
          />
        </GenericBottomSheet>

        {auctionCta.isAuction && (
          <GenericBottomSheet
            className="artwork-auction__bottom-sheet"
            open={auctionOpen}
            close={() => setAuctionOpen(false)}
            onTouchStart={(event) => event.stopPropagation()}
            onTouchEnd={(event) => event.stopPropagation()}
            onWheel={(event) => event.stopPropagation()}
          >
            <ArtworkAuctionMobile
              open={auctionOpen}
              onClose={() => setAuctionOpen(false)}
              artworkData={artworkSlides[activeSlide]}
              onUnlockSoldPrice={unlockSoldPrice}
              soldPricePending={soldPricePendingSlug === activeArtworkData?.slug}
            />
          </GenericBottomSheet>
        )}
        {isMobileLayout ? (
          <LimitedEditionOwnersPopupMobile
            open={ownersPopupOpen}
            onClose={closeOwnersPopup}
            owners={limitedEditionCollectors.owners}
            editionSizes={limitedEditionSizeMap}
            editionSoldCounts={limitedEditionCollectors.sold_counts}
            forceSequentialEditionNumbers={isAdminOnlyArtwork}
            forceDiscontinuedCounts={isAdminOnlyArtwork}
            discontinuedTotal={isAdminOnlyArtwork ? limitedEditionCollectors.sold_count : null}
            onNavigateProfile={(username) => navigate(`/user/${username}`)}
          />
        ) : (
          <LimitedEditionOwnersPopup
            open={ownersPopupOpen}
            onClose={closeOwnersPopup}
            owners={limitedEditionCollectors.owners}
            editionSizes={limitedEditionSizeMap}
            editionSoldCounts={limitedEditionCollectors.sold_counts}
            forceSequentialEditionNumbers={isAdminOnlyArtwork}
            forceDiscontinuedCounts={isAdminOnlyArtwork}
            discontinuedTotal={isAdminOnlyArtwork ? limitedEditionCollectors.sold_count : null}
            onNavigateProfile={(username) => navigate(`/user/${username}`)}
          />
        )}
        <SuartePlusPopup
          open={suartePlusPopupOpen}
          onClose={closeSuartePlusPopup}
          onSuccess={handleSuartePlusActivated}
          highlightedBenefit="limited_edition_collectors"
        />
        <SuartePlusPopup
          open={soldPricePopupOpen}
          onClose={closeSoldPricePopup}
          onSuccess={refreshSoldPriceAfterPlus}
          highlightedBenefit="sold_artwork_prices"
        />
      </>
    );
  }
}
